import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import toast from "react-hot-toast";

import { tg } from "@/constants/telegram";
import {
  useGetTrainerChatMessagesMutation,
  useGetTrainerChatMutation,
  useGetTrainerChatsMutation,
  useSendMessageMutation,
} from "@/store/usersApi";
import {
  AdminChatPayload,
  AdminChatUser,
  ChatMessage,
} from "@/features/chats";

type TrainerChatsContextValue = {
  initData?: string;
  chats: AdminChatUser[];
  chatsLoading: boolean;
  ensureChats: () => Promise<void>;
  isChatsLoaded: boolean;
  getChat: (userId: number) => Promise<AdminChatPayload | null>;
  getCachedChat: (userId: number) => AdminChatPayload | undefined;
  getMessages: (userId: number, force?: boolean) => Promise<ChatMessage[] | null>;
  getCachedMessages: (userId: number) => ChatMessage[] | undefined;
  sendSupportMessage: (userId: number, text?: string, image?: File) => Promise<void>;
  isSending: boolean;
};

const TrainerChatsContext = createContext<TrainerChatsContextValue | undefined>(
  undefined,
);

export const useTrainerChats = () => {
  const context = useContext(TrainerChatsContext);

  if (!context) {
    throw new Error(
      "useTrainerChats must be used within a TrainerChatsProvider",
    );
  }

  return context;
};

export const TrainerChatsProvider = ({ children }: { children: ReactNode }) => {
  const [initData, setInitData] = useState<string | undefined>(undefined);
  const [chats, setChats] = useState<AdminChatUser[]>([]);
  const [chatsLoading, setChatsLoading] = useState(false);
  const [chatsLoaded, setChatsLoaded] = useState(false);
  const [chatCache, setChatCache] = useState<Record<number, AdminChatPayload>>(
    {},
  );
  const [messagesCache, setMessagesCache] = useState<
    Record<number, ChatMessage[]>
  >({});

  const [getChats] = useGetTrainerChatsMutation();
  const [getChat] = useGetTrainerChatMutation();
  const [getChatMessages] = useGetTrainerChatMessagesMutation();
  const [sendMessageMutation, { isLoading: isSending }] =
    useSendMessageMutation();

  useEffect(() => {
    if (!initData) {
      tg.ready();
      setInitData(tg?.initData);

      if (window.Telegram?.WebApp) {
        window.Telegram.WebApp.expand();
      }
    }
  }, [initData]);

  const ensureChats = useCallback(async () => {
    if (!initData || chatsLoading || chatsLoaded) {
      return;
    }

    setChatsLoading(true);

    try {
      const result = await getChats().unwrap();
      setChats(result);
      setChatsLoaded(true);
    } catch (error: any) {
      if (error?.status === 403) {
        window.location.href = "/";
        return;
      }

      toast.error("Не удалось загрузить список чатов");
      console.error("getTrainerChats error", error);
    } finally {
      setChatsLoading(false);
    }
  }, [chatsLoaded, chatsLoading, getChats, initData]);

  const fetchChat = useCallback(
    async (userId: number): Promise<AdminChatPayload | null> => {
      if (chatCache[userId]) {
        return chatCache[userId];
      }

      if (!initData) {
        return null;
      }

      try {
        const data = await getChat({
          user_id: userId,
        }).unwrap();

        setChatCache((prev) => ({ ...prev, [userId]: data }));
        return data;
      } catch (error: any) {
        if (error?.status === 403) {
          window.location.href = "/";
          return null;
        }

        toast.error("Не удалось загрузить данные пользователя");
        console.error("getTrainerChat error", error);
        return null;
      }
    },
    [chatCache, getChat, initData],
  );

  const fetchMessages = useCallback(
    async (
      userId: number,
      force = false,
    ): Promise<ChatMessage[] | null> => {
      if (!force && messagesCache[userId]) {
        return messagesCache[userId];
      }

      if (!initData) {
        return null;
      }

      try {
        const data = await getChatMessages({
          user_id: userId,
        }).unwrap();

        setMessagesCache((prev) => ({ ...prev, [userId]: data }));
        return data;
      } catch (error: any) {
        if (error?.status === 403) {
          window.location.href = "/";
          return null;
        }

        toast.error("Не удалось загрузить сообщения");
        console.error("getTrainerChatMessages error", error);
        return null;
      }
    },
    [getChatMessages, initData, messagesCache],
  );

  const sendSupportMessage = useCallback(
    async (userId: number, text?: string, image?: File) => {
      if (!initData) {
        return;
      }

      if (!text && !image) {
        toast.error("Необходимо указать текст или изображение");
        return;
      }

      try {
        await sendMessageMutation({
          user_id: userId,
          msg: text,
          image: image,
        }).unwrap();

        // Формируем контент сообщения для отображения
        let messageContent = text || "";
        if (image) {
          // Создаем временный URL для превью изображения
          const imageUrl = URL.createObjectURL(image);
          if (messageContent) {
            messageContent = `${messageContent}\n<img>${imageUrl}</img>`;
          } else {
            messageContent = `<img>${imageUrl}</img>`;
          }
        }

        setMessagesCache((prev) => {
          const prevMessages = prev[userId] ?? [];
          const newMessage: ChatMessage = {
            id: Date.now(),
            chat_id: userId,
            user_id: userId,
            author: "support-user",
            content: messageContent,
            of_date: new Date().toISOString(),
          };

          return {
            ...prev,
            [userId]: [...prevMessages, newMessage],
          };
        });
      } catch (error: any) {
        if (error?.status === 403) {
          window.location.href = "/";
          return;
        }

        toast.error("Не удалось отправить сообщение");
        console.error("sendMessage error", error);
      }
    },
    [initData, sendMessageMutation],
  );

  const value = useMemo<TrainerChatsContextValue>(
    () => ({
      initData,
      chats,
      chatsLoading,
      ensureChats,
      isChatsLoaded: chatsLoaded,
      getChat: fetchChat,
      getCachedChat: (userId: number) => chatCache[userId],
      getMessages: fetchMessages,
      getCachedMessages: (userId: number) => messagesCache[userId],
      sendSupportMessage,
      isSending,
    }),
    [
      initData,
      chats,
      chatsLoading,
      ensureChats,
      chatsLoaded,
      fetchChat,
      chatCache,
      fetchMessages,
      messagesCache,
      sendSupportMessage,
      isSending,
    ],
  );

  return (
    <TrainerChatsContext.Provider value={value}>
      {children}
    </TrainerChatsContext.Provider>
  );
};




